<script setup>
const honeypot = ref("");
const message = ref("");

const props = defineProps({
  minFillTime: {
    type: Number,
    default: 1_000,
  },
  cooldownMs: {
    type: Number,
    default: 10_000,
  },
  displayMessage: {
    type: Boolean,
    default: false,
  },
});

defineExpose({ validate });

// --- timing & honeypot checks ---
const startTime = ref(0);

// --- math challenge state ---
const showChallenge = ref(false);
const challengeAnswer = ref(null);
const challenge = reactive({ a: 0, b: 0 });

// Compute the question text:
const challengeQuestion = computed(() => `${challenge.a} + ${challenge.b} = ?`);

const challengeSolved = ref(false);

function checkChallenge() {
  challengeSolved.value = false;

  if (challengeAnswer.value === challenge.a + challenge.b) {
    challengeSolved.value = true;
    message.value = "Poprawna odpowiedź! Możesz teraz wysłać formularz.";
    // Wait a bit before closing the challenge
    setTimeout(() => {
      showChallenge.value = false;
    }, 2_000);

    return;
  }

  message.value = "Niepoprawna odpowiedź. Proszę spróbować ponownie.";
  generateChallenge();
}

onMounted(() => {
  startTime.value = Date.now();
});

function generateChallenge() {
  challenge.a = Math.floor(Math.random() * 10) + 1;
  challenge.b = Math.floor(Math.random() * 10) + 1;
}

// --- submission handler ---
function validate() {
  const now = Date.now();
  let lastSubmit = parseInt(localStorage.getItem("lastContactSubmit") || "0");

  const slowEnough = now - startTime.value > props.minFillTime;
  const cooldownPassed = now - lastSubmit > props.cooldownMs;

  if (challengeSolved.value) {
    localStorage.setItem("lastContactSubmit", Date.now());
    showChallenge.value = false;
    return true;
  }

  if (!honeypot.value && slowEnough && cooldownPassed) {
    localStorage.setItem("lastContactSubmit", Date.now());
    showChallenge.value = false;
    return true;
  }

  if (honeypot.value) {
    message.value = "Możliwe, że używasz automatycznego wypełniacza formularzy.";
  }
  if (!slowEnough) {
    message.value = "Bardzo szybkie wypełnienie formularza. Proszę spróbować ponownie.";
  }
  if (!cooldownPassed) {
    message.value = `Minimalny czas pomiędzy wysłaniami wynosi ${
      props.cooldownMs / 1_000
    } sekund. Proszę spróbować ponownie później.`;
  }

  showChallenge.value = true;
  generateChallenge();

  return false;
}
</script>

<template>
  <div>
    <!-- popup window -->
    <label for="surname" class="h-label">Nazwisko (pole ukryte)</label>
    <input
      id="surname"
      name="surname"
      v-model="honeypot"
      type="text"
      autocomplete="off"
      class="bg-white border rounded p-2 mb-3"
      tabindex="-1"
    />

    <Transition name="pop-in">
      <div
        v-if="showChallenge"
        class="fixed top-0 left-0 w-screen h-screen bg-black/25 flex items-center justify-center cursor-pointer"
        @click.self="showChallenge = false"
      >
        <div
          class="bg-white max-w-4xl rounded p-8 cursor-default flex flex-col items-center gap-4"
        >
          <!-- We dectected sus activity -->
          <strong v-if="challengeSolved != true" class="text-xl font-bold text-center"
            >Wykryto podejrzaną aktywność</strong
          >

          <!-- Math challenge, shown only if filling was too fast -->
          <div v-if="challengeSolved != true" class="">
            <label class="block mb-1 text-center">{{ challengeQuestion }}</label>

            <div class="flex flex-row gap-4">
              <input
                v-model.number="challengeAnswer"
                type="number"
                required
                class="w-full border rounded px-2"
              />
              <button
                @click="checkChallenge"
                class="bg-blue-600 text-white rounded hover:bg-blue-700 p-2 cursor-pointer"
              >
                Sprawdź
              </button>
            </div>
          </div>

          <!-- Message -->
          <div v-if="message && displayMessage">
            {{ message }}
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.pop-in-enter-active,
.pop-in-leave-active {
  transition: opacity 1000ms ease, transform 1000ms ease;
}

.pop-in-enter-from,
.pop-in-leave-to {
  opacity: 0;
}

.pop-in-enter-to,
.pop-in-leave-from {
  opacity: 1;
  transform: scale(1);
}
/* Optional: styling to hide honeypot field accessibly */
[tabindex="-1"] {
  position: absolute;
  left: -9999px;
}

/* Inteligently hidden label */
.h-label {
  position: absolute;
  left: -9999px;
  top: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
  white-space: nowrap;
}
</style>
